import React, { useState } from "react";
import { ImageOff, Loader2 } from "lucide-react";

/**
 * SmartImage — Hidden Gem universal image renderer.
 *
 * The one component every user-uploaded image should flow through. It:
 *   1. Preserves aspect ratio (never stretches)
 *   2. Defaults to `smart_auto_fit`: image is shown in full (object-contain)
 *      with a subtle blurred copy of itself filling any letterbox space —
 *      so it feels premium and integrated, not like black bars.
 *   3. Accepts `mode="fill_crop"` when the caller wants cover-cropping
 *      (e.g. avatars, small product tiles).
 *   4. Handles loading + broken-image states so a bad upload never breaks
 *      the surrounding page.
 *   5. `object-position` respects the user's manual reposition if provided.
 *
 * Props:
 *   src           — resolved image URL (already through fileUrl)
 *   alt           — alt text
 *   mode          — "smart_auto_fit" (default) | "fill_crop"
 *   aspect        — Tailwind aspect class, e.g. "aspect-square", "aspect-[3/1]".
 *                   Keeps the container shape stable even when the image is
 *                   letterboxed. If omitted, the caller controls sizing.
 *   position      — object-position CSS string, e.g. "50% 30%" (defaults center)
 *   className     — extra classes for the outer wrapper
 *   imgClassName  — extra classes on the <img> itself (rare)
 *   rounded       — Tailwind rounded class (defaults to none; caller wraps)
 *   loading       — "eager" | "lazy" — passes through to <img>
 *   testid        — data-testid on the wrapper
 *   fallback      — element to render if src is falsy or errors
 */
export default function SmartImage({
  src,
  alt = "",
  mode = "smart_auto_fit",
  aspect,
  position = "center",
  className = "",
  imgClassName = "",
  rounded = "",
  loading = "lazy",
  testid,
  fallback = null,
}) {
  const [state, setState] = useState("loading"); // loading | ok | error

  if (!src && !fallback) return null;

  const wrapperCls = [
    "relative overflow-hidden bg-zinc-900",
    aspect || "",
    rounded,
    className,
  ].filter(Boolean).join(" ");

  if (!src) {
    return <div className={wrapperCls} data-testid={testid}>{fallback}</div>;
  }

  const isFill = mode === "fill_crop";
  return (
    <div
      className={wrapperCls}
      data-testid={testid}
      data-image-mode={mode}
    >
      {/* Blurred backdrop — only for smart_auto_fit, and hidden while loading */}
      {!isFill && (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-60 pointer-events-none transition-opacity duration-300 ${state === "ok" ? "opacity-60" : "opacity-0"}`}
          loading="lazy"
          draggable={false}
        />
      )}

      {/* Foreground image */}
      {state !== "error" && (
        <img
          src={src}
          alt={alt}
          loading={loading}
          onLoad={() => setState("ok")}
          onError={() => setState("error")}
          draggable={false}
          className={[
            "relative w-full h-full transition-opacity duration-300",
            isFill ? "object-cover" : "object-contain",
            state === "ok" ? "opacity-100" : "opacity-0",
            imgClassName,
          ].join(" ")}
          style={{ objectPosition: position }}
        />
      )}

      {/* Loading spinner */}
      {state === "loading" && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
        </div>
      )}

      {/* Error fallback */}
      {state === "error" && (
        <div className="absolute inset-0 grid place-items-center bg-zinc-900 text-zinc-500 gap-1 flex-col text-[10px] uppercase tracking-widest font-bold">
          <ImageOff className="w-5 h-5" />
          <span>Image unavailable</span>
        </div>
      )}
    </div>
  );
}
